import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import {
  AZURE_VENDOR_ID,
  BEDROCK_VENDOR_ID,
  DEFAULT_AZURE_API_VERSION,
  DEFAULT_OPENAI_BASE_URL,
  GITHUB_COPILOT_VENDOR_ID,
  VERTEX_VENDOR_ID,
  VendorId,
  isHttpsUrl,
  normalizeBaseUrl,
} from './byok-vendor';

/**
 * Per-vendor credential-field descriptors.
 *
 * The BYOK form is rendered, validated and serialised from {@link PROVIDER_FIELDS} — there is no
 * per-vendor `@if` ladder in the template and no per-vendor branch in the submit path. A field that
 * is not in the active vendor's descriptor list is not rendered, not validated, and stripped from
 * the payload. Adding a fifth vendor is one entry in this map.
 */

/**
 * How a field is rendered. `deployments` is the Azure `FormArray` sub-editor; `model-bindings` is
 * the Vertex `FormArray` sub-editor (publisher-model → logical-model rows) — the same editor shape
 * generalised to a different row schema. `sa-key-json` is a write-only multi-line JSON textarea (the
 * Vertex service-account key), rendered as a `<textarea>` and never echoed back after submit.
 */
export type ByokFieldType =
  | 'text'
  | 'url'
  | 'deployments'
  | 'model-bindings'
  | 'sa-key-json'
  // Bedrock-only. `aws-region` is a SELECT, not free text: the region is a hard routing property and
  // a value outside the server-owned allow-list is a 400, so a typo must be unrepresentable rather
  // than merely rejected. Its options come from the server (`GET .../byok/bedrock/catalog`), never
  // from a constant here. `bedrock-model-bindings` is the region-filtered binding PICKER — the admin
  // SELECTS catalog rows and can never AUTHOR a pair, which is what stops an expensive model being
  // mapped to a cheap logical id and understating spend.
  | 'aws-region'
  | 'bedrock-model-bindings'
  // GitHub Copilot-only. `github-copilot-pat` is a WRITE-ONLY password input for a fine-grained
  // personal access token — rendered like any other secret, but never pre-filled and never echoed,
  // because no read path returns it in any form.
  //
  // `github-copilot-models` is a SELECTION from the org's own DISCOVERED catalog, never free text
  // (plan D-C5). The distinction from `bedrock-model-bindings` is not cosmetic: Bedrock's catalog is
  // a server-owned CONSTANT of what an admin MAY register, so it can be read before registering.
  // Copilot's is what THIS org's credential can actually see, discovered by the server calling
  // `listModels()` with it — so it does not exist until the registration ACTIVATES, and this field
  // is therefore a POST-activation view, never part of the onboarding submit.
  | 'github-copilot-pat'
  | 'github-copilot-models';

/** Every control the single BYOK `FormGroup` carries. `vendor` is the switch, so it is not a field. */
export type ByokFieldKey =
  | 'apiKey'
  | 'displayName'
  | 'baseUrl'
  | 'apiVersion'
  | 'deployments'
  // Vertex-only: the SA-key-vs-WIF sub-switch. Like `vendor` it selects the field set rather than
  // being a rendered field, so it carries no `ByokFieldSpec` in PROVIDER_FIELDS.
  | 'authMode'
  // Vertex-only, all non-secret routing coordinates:
  | 'projectId'
  | 'location'
  | 'serviceAccountEmail'
  | 'workloadIdentityProvider'
  | 'audience'
  // Vertex service-account-key mode only: the WRITE-ONLY JSON key.
  | 'serviceAccountKeyJson'
  // Bedrock-only, both non-secret:
  | 'region'
  | 'expiresAt'
  | 'models'
  // GitHub Copilot only. `personalAccessToken` (PAT mode) and `privateKeyPem` (GITHUB_APP mode, Stage
  // 2) are WRITE-ONLY; the rest are non-secret App coordinates. Like Vertex's `authMode`, Copilot's
  // own auth-mode switch selects the field set rather than being a rendered field, so it carries no
  // spec here and reuses the `authMode` key.
  | 'personalAccessToken'
  | 'appId'
  | 'installationId'
  | 'privateKeyPem'
  | 'anchorRepositoryId'
  | 'copilotModels';

/**
 * The Copilot-only subset of {@link ByokFieldKey}.
 *
 * Declared here, beside the keys themselves, so the generic BYOK card can exclude them in ONE
 * place. Copilot is not a generic-card vendor — `GENERIC_BYOK_VENDOR_IDS` omits it structurally
 * because its registration is two-phase (see `byok-github-copilot-card`) — so the single generic
 * `FormGroup` deliberately carries no control for any of these keys, and a future Copilot key added
 * to `ByokFieldKey` must be added here too or the generic card stops compiling.
 */
export type GitHubCopilotFieldKey =
  | 'personalAccessToken'
  | 'appId'
  | 'installationId'
  | 'privateKeyPem'
  | 'anchorRepositoryId'
  | 'copilotModels';

/**
 * Vertex's two onboarding auth modes, selected by a required `authMode` field.
 *
 * - `service_account_key` — **the primary / default** mode. The customer supplies a service-account
 *   JSON key; `projectId` and `serviceAccountEmail` are derived server-side from the key, so the UI
 *   asks for neither, and the WIF fields are absent.
 * - `workload_identity` — the enterprise **alternative**: no stored secret, routes through GCP
 *   Workload Identity Federation using project / SA-email / WIF-provider / audience.
 *
 * Order drives the mode toggle (SA-key is the quick-start tab, WIF the advanced tab).
 */
export const VERTEX_AUTH_MODES = ['service_account_key', 'workload_identity'] as const;
export type VertexAuthMode = (typeof VERTEX_AUTH_MODES)[number];

/** The primary / default Vertex onboarding mode (quick-start, key-based). */
export const DEFAULT_VERTEX_AUTH_MODE: VertexAuthMode = 'service_account_key';

/**
 * Type-or-pick suggestions for the Vertex `location` field. Mirrors the backend's location
 * allow-list (`GcpLocationPolicy.ALLOWED`, `TokenHubPolicies.kt`) — suggestions only: the backend
 * validates the submitted value, so drift here can never widen what registers, only make the
 * dropdown incomplete. Common regions first, then alphabetical.
 */
export const VERTEX_LOCATION_SUGGESTIONS: readonly string[] = [
  'global',
  'us-central1',
  'europe-west1',
  'europe-west2',
  'asia-east1',
  'asia-east2',
  'asia-northeast1',
  'asia-northeast3',
  'asia-south1',
  'asia-southeast1',
  'australia-southeast1',
  'europe-central2',
  'europe-north1',
  'europe-southwest1',
  'europe-west3',
  'europe-west4',
  'europe-west9',
  'northamerica-northeast1',
  'southamerica-east1',
  'us-east1',
  'us-east4',
  'us-east5',
  'us-south1',
  'us-west1',
  'us-west4',
];

/**
 * Type-or-pick suggestions for the model-binding publisher/logical model-id inputs. Mirrors the
 * server-owned `VertexModelCatalog` (the admission validator — only catalog pairs can activate),
 * so these are the ids that will actually register today; free text stays allowed for forward
 * compatibility with catalog additions.
 */
export const VERTEX_MODEL_ID_SUGGESTIONS: readonly string[] = [
  'gemini-2.5-flash',
  'gemini-2.5-pro',
  'gemini-3.1-pro-preview',
  'gemini-3.5-flash',
];

/**
 * GitHub Copilot's two credential modes, selected by a required `authMode` — the Vertex shape, not
 * Bedrock's (which has exactly one mode and therefore no discriminator at all).
 *
 * - `PAT` — **the only mode Stage 1 implements.** A fine-grained personal access token
 *   (`github_pat_`) with Copilot Requests = Read, owned by a USER (an org-owned token does not
 *   expose the permission). Billing and rate limits are the TOKEN OWNER'S subscription.
 * - `GITHUB_APP` — Stage 2. Declared now so the contract schema never changes, and hidden in the
 *   UI unless the backend reports `phx.github-copilot.app-auth-enabled`. There is deliberately no
 *   frontend flag for it: a second gate the browser owns could disagree with the server's, and the
 *   server's is the one that decides whether a submission is accepted.
 *
 * Order drives the mode toggle.
 */
export const GITHUB_COPILOT_FIELD_AUTH_MODES = ['PAT', 'GITHUB_APP'] as const;
export type GitHubCopilotFieldAuthMode = (typeof GITHUB_COPILOT_FIELD_AUTH_MODES)[number];

/** The primary — and in Stage 1 the only implemented — Copilot credential mode. */
export const DEFAULT_GITHUB_COPILOT_FIELD_AUTH_MODE: GitHubCopilotFieldAuthMode = 'PAT';

export interface ByokFieldSpec {
  readonly key: ByokFieldKey;
  readonly label: string;
  readonly hint: string;
  readonly required: boolean;
  /** Value seeded into a blank control when this vendor becomes active. */
  readonly default: string;
  readonly validators: readonly ValidatorFn[];
  readonly type: ByokFieldType;
  readonly placeholder?: string;
  /**
   * Optional type-or-pick suggestions rendered as a native `<datalist>` on the input. Purely a
   * convenience — free text stays allowed and the backend remains the validator, so a stale or
   * incomplete list here can never block a legitimate value.
   */
  readonly suggestions?: readonly string[];
  /** Render as a password input and never echo the value back into the form after submit. */
  readonly secret?: boolean;
  /**
   * Vertex-only: which auth mode(s) this field belongs to. Absent means "both modes" (displayName,
   * location, model bindings). Non-Vertex vendors never set this and it is ignored for them — only
   * {@link vertexFieldsForMode} consults it.
   */
  readonly modes?: readonly VertexAuthMode[];
  /**
   * GitHub Copilot-only: which auth mode(s) this field belongs to. Absent means "both modes"
   * (displayName). A separate property from {@link modes} rather than a widened union, because the
   * two vendors' mode vocabularies are disjoint and sharing one property would make
   * `vertexFieldsForMode('PAT')` type-check while returning nonsense. Only
   * {@link githubCopilotFieldsForMode} consults it.
   */
  readonly copilotModes?: readonly GitHubCopilotFieldAuthMode[];
}

// ── Validators ────────────────────────────────────────────────────────────────

/**
 * Required, treating a whitespace-only value as empty.
 *
 * `Validators.required` passes `'   '` (any non-empty string is "present"), which let a
 * whitespace-only Azure resource URL clear validation and then get stripped from the payload as
 * blank — registering a credential with no upstream. Emits the standard `required` key so error
 * mapping is unchanged.
 */
export const requiredTrimmed: ValidatorFn = (control: AbstractControl): ValidationErrors | null =>
  String(control.value ?? '').trim() ? null : { required: true };

/** {@link Validators.minLength} measured after trimming, for the same reason as above. */
export function minTrimmedLength(min: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const length = String(control.value ?? '').trim().length;
    if (length === 0 || length >= min) return null; // emptiness is `required`'s business
    return { minlength: { requiredLength: min, actualLength: length } };
  };
}

/**
 * `https:`-only, well-formed URL. Empty passes — `required` is what makes a field mandatory, so a
 * blank optional URL is valid.
 *
 * UX only. The server-side SSRF/host allow-list is the real control (G5).
 */
export const httpsUrlValidator: ValidatorFn = (
  control: AbstractControl,
): ValidationErrors | null => {
  const value = normalizeBaseUrl(control.value as string);
  if (!value) return null;
  return isHttpsUrl(value) ? null : { httpsUrl: true };
};

/**
 * Validates the Azure deployments `FormArray` as a whole rather than per row, so the three rules
 * produce distinct, quotable error keys:
 *
 * - `noDeployments` — no row has both fields. Azure has no default deployment, so a credential with
 *   none can never route a call.
 * - `incompleteDeployment` — a row has one field but not the other. Silently dropping it would
 *   register a credential missing a deployment the admin believed they had added.
 * - `duplicateDeploymentName` / `duplicateLogicalModelId` — case-insensitively repeated. Two rows
 *   with the same deployment name are ambiguous to route; two with the same logical model id make
 *   the model picker ambiguous to price.
 *
 * An entirely blank row is ignored (an admin who clicked "Add deployment" and changed their mind
 * isn't blocked); it is stripped from the payload too.
 */
export const deploymentsValidator: ValidatorFn = (
  control: AbstractControl,
): ValidationErrors | null => {
  const rows = (control.value ?? []) as ReadonlyArray<{
    deploymentName?: string | null;
    logicalModelId?: string | null;
  }>;
  const trimmed = rows.map((row) => ({
    deploymentName: String(row?.deploymentName ?? '').trim(),
    logicalModelId: String(row?.logicalModelId ?? '').trim(),
  }));
  const touched = trimmed.filter((row) => row.deploymentName || row.logicalModelId);
  if (touched.length === 0) return { noDeployments: true };
  if (touched.some((row) => !row.deploymentName || !row.logicalModelId)) {
    return { incompleteDeployment: true };
  }
  if (hasCaseInsensitiveDuplicate(touched.map((row) => row.deploymentName))) {
    return { duplicateDeploymentName: true };
  }
  if (hasCaseInsensitiveDuplicate(touched.map((row) => row.logicalModelId))) {
    return { duplicateLogicalModelId: true };
  }
  return null;
};

function hasCaseInsensitiveDuplicate(values: readonly string[]): boolean {
  const seen = new Set(values.map((value) => value.toLowerCase()));
  return seen.size !== values.length;
}

/**
 * Validates the Vertex model-bindings `FormArray` as a whole, mirroring {@link deploymentsValidator}
 * on the Vertex row schema `{ providerModelId, logicalModelId }`:
 *
 * - `noModelBindings` — no row has both fields. Vertex has no default model, so a registration with
 *   none can never route a call (the backend rejects an empty `models` list too).
 * - `incompleteModelBinding` — a row has one field but not the other. Dropping it silently would
 *   register a binding the admin believed they had added.
 * - `duplicateProviderModelId` / `duplicateLogicalModelId` — case-insensitively repeated: two rows
 *   with the same publisher model id are ambiguous to route; two with the same logical id make the
 *   model picker ambiguous to price.
 *
 * An entirely blank row is ignored and stripped from the payload.
 */
export const modelBindingsValidator: ValidatorFn = (
  control: AbstractControl,
): ValidationErrors | null => {
  const rows = (control.value ?? []) as ReadonlyArray<{
    providerModelId?: string | null;
    logicalModelId?: string | null;
  }>;
  const trimmed = rows.map((row) => ({
    providerModelId: String(row?.providerModelId ?? '').trim(),
    logicalModelId: String(row?.logicalModelId ?? '').trim(),
  }));
  const touched = trimmed.filter((row) => row.providerModelId || row.logicalModelId);
  if (touched.length === 0) return { noModelBindings: true };
  if (touched.some((row) => !row.providerModelId || !row.logicalModelId)) {
    return { incompleteModelBinding: true };
  }
  if (hasCaseInsensitiveDuplicate(touched.map((row) => row.providerModelId))) {
    return { duplicateProviderModelId: true };
  }
  if (hasCaseInsensitiveDuplicate(touched.map((row) => row.logicalModelId))) {
    return { duplicateLogicalModelId: true };
  }
  return null;
};

/**
 * Validates the Bedrock model-bindings `FormArray`. The row schema is `{ providerModelId,
 * logicalModelId }`, identical to Vertex's, but the failure modes are not the same and the messages
 * must not be either — Bedrock rows are SELECTED from a server-owned region-filtered catalog rather
 * than typed, so "add at least one" means "tick a model", not "type a model id".
 *
 * - `noModelBindings` — nothing selected. Bedrock has no default model, and the backend rejects an
 *   empty `models` list, so a registration with none can never route a call.
 * - `duplicateProviderModelId` / `duplicateLogicalModelId` — a duplicate is unreachable through the
 *   picker (a catalog row is either selected or not), so this only fires if a row is seeded from a
 *   stored registration that already contains one. Kept for exactly that case rather than assumed
 *   impossible.
 *
 * There is deliberately NO `incompleteModelBinding` case: the picker writes both ids together from
 * one catalog row, so a half-filled row cannot exist. A row missing either id would mean the picker
 * itself is broken, and {@link collectBedrockModelBindings} drops it rather than sending half a pair.
 */
export const bedrockModelBindingsValidator: ValidatorFn = (
  control: AbstractControl,
): ValidationErrors | null => {
  const rows = (control.value ?? []) as ReadonlyArray<{
    providerModelId?: string | null;
    logicalModelId?: string | null;
  }>;
  const complete = rows
    .map((row) => ({
      providerModelId: String(row?.providerModelId ?? '').trim(),
      logicalModelId: String(row?.logicalModelId ?? '').trim(),
    }))
    .filter((row) => row.providerModelId && row.logicalModelId);
  if (complete.length === 0) return { noModelBindings: true };
  if (hasCaseInsensitiveDuplicate(complete.map((row) => row.providerModelId))) {
    return { duplicateProviderModelId: true };
  }
  if (hasCaseInsensitiveDuplicate(complete.map((row) => row.logicalModelId))) {
    return { duplicateLogicalModelId: true };
  }
  return null;
};

/** First human-readable message for a BEDROCK model-bindings error, or null when valid. */
export function bedrockModelBindingsErrorText(errors: ValidationErrors | null): string | null {
  if (!errors) return null;
  if (errors['noModelBindings']) return 'Select at least one model for this region.';
  if (errors['duplicateProviderModelId']) return 'Each Bedrock model may only be selected once.';
  if (errors['duplicateLogicalModelId']) {
    return 'Two selected models map to the same logical model id — pick only one of them.';
  }
  return null;
}

/**
 * Optional ISO-8601 instant (`2027-08-31T00:00:00Z`). Empty passes — the key's expiry is optional
 * because AWS does not always surface one to the customer.
 *
 * The backend parses this STRICTLY as an `Instant`, so a bare date (`2027-08-31`) or a local
 * date-time with no zone is a 400. Checking it here turns a round-trip rejection into an inline
 * message; the backend remains the authority either way.
 */
export const isoInstantValidator: ValidatorFn = (
  control: AbstractControl,
): ValidationErrors | null => {
  const value = String(control.value ?? '').trim();
  if (!value) return null;
  // Require an explicit zone designator: `Date.parse` alone accepts a zoneless value the backend's
  // `Instant.parse` refuses, which is precisely the mistake this validator exists to catch early.
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?(\.\d+)?(Z|[+-]\d{2}:\d{2})$/.test(value)) {
    return { isoInstant: true };
  }
  return Number.isNaN(Date.parse(value)) ? { isoInstant: true } : null;
};

/**
 * A GitHub **fine-grained** personal access token (`github_pat_` prefix).
 *
 * Empty passes — `required` is what makes a field mandatory. The two failure keys are distinct on
 * purpose, because the remedies are completely different and telling them apart is the whole value
 * of checking here rather than waiting for the round trip:
 *
 *  - `classicGitHubPat` — a `ghp_` token. GitHub deprecated these AND they do not expose the Copilot
 *    Requests permission at any level, so no amount of re-scoping one will work: the user must
 *    generate a fine-grained token instead.
 *  - `gitHubPatPrefix` — anything else. Usually a paste of the wrong value entirely.
 *
 * **UX only.** The backend rejects a classic token with a typed 400 BEFORE any network call, and is
 * the authority either way; this exists so the admin is told before submitting. It deliberately does
 * NOT check length, character set or entropy: GitHub does not document a stable token length, and a
 * client-side guess that rejected a legitimate token would be worse than no check at all.
 */
export const fineGrainedGitHubPatValidator: ValidatorFn = (
  control: AbstractControl,
): ValidationErrors | null => {
  const value = String(control.value ?? '').trim();
  if (!value) return null;
  if (value.startsWith('ghp_')) return { classicGitHubPat: true };
  return value.startsWith('github_pat_') ? null : { gitHubPatPrefix: true };
};

// ── Shared field specs ────────────────────────────────────────────────────────

/** Minimum plausible provider key length. Cheap client-side guard against a truncated paste. */
export const MIN_API_KEY_LENGTH = 10;

const API_KEY_FIELD: ByokFieldSpec = {
  key: 'apiKey',
  label: 'API Key',
  hint: `At least ${MIN_API_KEY_LENGTH} characters. Encrypted at rest server-side — the raw secret is never shown after saving.`,
  required: true,
  default: '',
  validators: [requiredTrimmed, minTrimmedLength(MIN_API_KEY_LENGTH)],
  type: 'text',
  placeholder: 'Provider API key',
  secret: true,
};

const DISPLAY_NAME_FIELD: ByokFieldSpec = {
  key: 'displayName',
  label: 'Label',
  hint: 'Optional. Helps tell two credentials apart in the list.',
  required: false,
  default: '',
  validators: [],
  type: 'text',
  placeholder: 'e.g. prod',
};

/**
 * Optional OpenAI-compatible gateway URL, shared by every non-Azure vendor. Blank means "use the
 * provider's own API" — the backend applies its default upstream.
 */
function gatewayBaseUrlField(placeholder: string): ByokFieldSpec {
  return {
    key: 'baseUrl',
    label: 'Base URL',
    hint: 'Optional. Point this at an OpenAI-compatible gateway (LiteLLM, vLLM, OpenRouter, a corporate proxy). Leave blank to call the provider directly.',
    required: false,
    default: '',
    validators: [httpsUrlValidator],
    type: 'url',
    placeholder,
  };
}

// ── The map ───────────────────────────────────────────────────────────────────

export const PROVIDER_FIELDS: Record<VendorId, readonly ByokFieldSpec[]> = {
  openai: [API_KEY_FIELD, DISPLAY_NAME_FIELD, gatewayBaseUrlField(DEFAULT_OPENAI_BASE_URL)],
  anthropic: [API_KEY_FIELD, DISPLAY_NAME_FIELD, gatewayBaseUrlField('https://api.anthropic.com')],
  gemini: [
    API_KEY_FIELD,
    DISPLAY_NAME_FIELD,
    gatewayBaseUrlField('https://generativelanguage.googleapis.com'),
  ],
  // Token Hub validates a supplied xAI baseUrl to exactly https://api.x.ai or
  // https://api.x.ai/v1 (docs/plans/grok.md §2) — blank leaves the backend default in force.
  xai: [API_KEY_FIELD, DISPLAY_NAME_FIELD, gatewayBaseUrlField('https://api.x.ai/v1')],
  [AZURE_VENDOR_ID]: [
    API_KEY_FIELD,
    DISPLAY_NAME_FIELD,
    {
      key: 'baseUrl',
      label: 'Azure resource URL',
      hint: 'Required. Must be an https Azure OpenAI or Cognitive Services host — the backend rejects anything else.',
      required: true,
      default: '',
      validators: [requiredTrimmed, httpsUrlValidator],
      type: 'url',
      placeholder: 'https://my-company-openai.openai.azure.com',
    },
    {
      key: 'apiVersion',
      label: 'API version',
      hint: 'Required. Azure data-plane API version for this resource.',
      required: true,
      default: DEFAULT_AZURE_API_VERSION,
      validators: [requiredTrimmed],
      type: 'text',
      placeholder: DEFAULT_AZURE_API_VERSION,
    },
    {
      key: 'deployments',
      label: 'Deployments',
      hint: 'The deployment name routes the request; the model id is what usage is priced against. At least one is required.',
      required: true,
      default: '',
      validators: [deploymentsValidator],
      type: 'deployments',
    },
  ],
  // Google Vertex AI — the org-managed provider. Mode-aware (see `modes`): the primary
  // `service_account_key` mode takes a WRITE-ONLY service-account JSON key (project id + service
  // account email are derived server-side from it); the alternative `workload_identity` mode is
  // secret-free and routes through GCP Workload Identity Federation. displayName / location / model
  // bindings are shared by both modes. Consume through `vertexFieldsForMode(authMode)`, never the raw
  // list. See docs/features/google-vertex-ai-provider.md.
  [VERTEX_VENDOR_ID]: [
    DISPLAY_NAME_FIELD, // both modes
    {
      key: 'projectId',
      label: 'GCP project ID',
      hint: 'Required. The Google Cloud project hosting Vertex AI.',
      required: true,
      default: '',
      validators: [requiredTrimmed],
      type: 'text',
      placeholder: 'customer-project-id',
      modes: ['workload_identity'],
    },
    {
      key: 'location',
      label: 'Location',
      hint: "Required. The Vertex AI region the registration is pinned to, or 'global'.",
      required: true,
      default: '',
      validators: [requiredTrimmed],
      type: 'text',
      placeholder: 'europe-west2',
      suggestions: VERTEX_LOCATION_SUGGESTIONS,
    },
    {
      key: 'serviceAccountEmail',
      label: 'Service account email',
      hint: 'Required. The customer service account Phoenix impersonates at runtime (no key/JSON needed).',
      required: true,
      default: '',
      validators: [requiredTrimmed],
      type: 'text',
      placeholder: 'vertex-runtime@customer-project.iam.gserviceaccount.com',
      modes: ['workload_identity'],
    },
    {
      key: 'workloadIdentityProvider',
      label: 'Workload Identity provider',
      hint: 'Required. The WIF provider resource name Phoenix federates through.',
      required: true,
      default: '',
      validators: [requiredTrimmed],
      type: 'text',
      placeholder:
        'projects/123456789/locations/global/workloadIdentityPools/phoenix-pool/providers/aws-provider',
      modes: ['workload_identity'],
    },
    {
      key: 'audience',
      label: 'WIF audience',
      hint: 'Required. The audience Phoenix presents at the STS token exchange.',
      required: true,
      default: '',
      validators: [requiredTrimmed],
      type: 'text',
      placeholder:
        '//iam.googleapis.com/projects/123456789/locations/global/workloadIdentityPools/phoenix-pool/providers/aws-provider',
      modes: ['workload_identity'],
    },
    {
      key: 'serviceAccountKeyJson',
      label: 'Service account key (JSON)',
      hint: 'Required. Paste the service-account JSON key. Project id and service account email are derived from it server-side. Encrypted at rest — never shown again after saving.',
      required: true,
      default: '',
      validators: [requiredTrimmed],
      type: 'sa-key-json',
      placeholder: '{\n  "type": "service_account",\n  "project_id": "…",\n  "private_key": "…"\n}',
      secret: true,
      modes: ['service_account_key'],
    },
    {
      key: 'models',
      label: 'Model bindings',
      hint: 'The publisher model id (e.g. gemini-2.5-pro) routes the request; the logical model id is what usage is priced against. At least one is required.',
      required: true,
      default: '',
      validators: [modelBindingsValidator],
      type: 'model-bindings',
    },
  ],
  // Amazon Bedrock — the second org-managed provider, and a DISJOINT SIBLING of Vertex (plan D-B8),
  // not a variant of it. Three things about this descriptor are load-bearing and easy to get wrong:
  //
  //  1. **There is no `authMode`.** Bedrock has exactly one auth mode, so carrying a discriminator
  //     would imply a second one that does not exist. The backend asserts its absence structurally.
  //  2. **The API key is WRITE-ONLY with NO hint at all — not even a tail.** Unlike every key vendor,
  //     Bedrock's masked status is region + bindings + expiry and carries no key material whatsoever,
  //     so the field is never pre-filled and the copy says "replace" rather than "rotate".
  //  3. **REGION DRIVES EVERYTHING.** The same logical model carries a DIFFERENT providerModelId per
  //     region (eu.anthropic.claude-sonnet-5 vs us.anthropic.claude-sonnet-5), and a cross-geography
  //     pair is refused at registration AND again at dispatch. The bindings picker is therefore
  //     filtered by the selected region, and changing the region INVALIDATES anything already chosen.
  //
  // There is deliberately no `baseUrl`: the upstream host derives from the registered region alone
  // and there is no caller-supplied endpoint parameter anywhere in the Bedrock path.
  [BEDROCK_VENDOR_ID]: [
    {
      key: 'region',
      label: 'AWS Region',
      hint: 'Required. The Region this registration is pinned to. It selects the upstream host AND which model ids are valid — changing it invalidates any models already selected below. Changing it later is a re-registration.',
      required: true,
      default: '',
      validators: [requiredTrimmed],
      type: 'aws-region',
      placeholder: 'Select a Region',
    },
    {
      key: 'apiKey',
      label: 'Bedrock API key',
      hint: `At least ${MIN_API_KEY_LENGTH} characters. A long-term Amazon Bedrock API key. Stored encrypted and WRITE-ONLY — it is never shown again, not even partially, so re-registering replaces it rather than editing it.`,
      required: true,
      default: '',
      validators: [requiredTrimmed, minTrimmedLength(MIN_API_KEY_LENGTH)],
      type: 'text',
      placeholder: 'Paste the Bedrock API key',
      secret: true,
    },
    {
      key: 'expiresAt',
      label: 'Key expiry',
      hint: 'Optional, but strongly recommended. A long-term Bedrock API key expires on a date you chose when you generated it; recording it here is what lets Phoenix warn you before the registration silently stops working. ISO-8601 with a zone, e.g. 2027-08-31T00:00:00Z.',
      required: false,
      default: '',
      validators: [isoInstantValidator],
      type: 'text',
      placeholder: '2027-08-31T00:00:00Z',
    },
    DISPLAY_NAME_FIELD,
    {
      key: 'models',
      label: 'Models',
      hint: 'Select the models this registration may use. The list is server-owned and filtered by the Region above; the id sent on the wire is fixed by the catalog, so it can never be mistyped or mapped to the wrong billing identity.',
      required: true,
      default: '',
      validators: [bedrockModelBindingsValidator],
      type: 'bedrock-model-bindings',
    },
  ],
  // GitHub Copilot — the fourth org-managed provider, a DISJOINT SIBLING of both Vertex and Bedrock
  // (plan section 0.5), and the one whose descriptor is smallest. Four things about it are
  // load-bearing and easy to get wrong:
  //
  //  1. **There is NO model field in the registration form, and that is not an omission.** Every
  //     other vendor's models are an INPUT the admin supplies; Copilot's are a RESULT the server
  //     discovers by calling `listModels()` with the credential (plan D-C5). There is nothing to
  //     pick before registering, so the `copilotModels` field below is a POST-activation view —
  //     `githubCopilotFieldsForMode` deliberately excludes it from both modes' form field sets.
  //  2. **Registration is TWO-PHASE.** Submitting this form creates a `pending` generation and
  //     nothing usable; a second `activate` call promotes it only once the server has listed models
  //     with that exact credential. A PAT belonging to an account with no Copilot seat passes every
  //     check in this file and is then refused by GitHub — observed live.
  //  3. **The token is FINE-GRAINED only.** Classic `ghp_` tokens are deprecated by GitHub and do
  //     not expose the Copilot Requests permission at all, so they are caught here (a typed inline
  //     message) and again by the backend (a typed 400, before any network call).
  //  4. **There is no `baseUrl`, no region, no project.** Copilot is not an inference API: a CLI
  //     child process composes and sends the request server-side, so there is no caller-supplied
  //     endpoint parameter anywhere in the path and never will be.
  [GITHUB_COPILOT_VENDOR_ID]: [
    DISPLAY_NAME_FIELD, // both modes
    {
      key: 'personalAccessToken',
      label: 'Personal access token',
      hint:
        'Required. A FINE-GRAINED GitHub token (github_pat_…) with Copilot Requests = Read, owned ' +
        'by a user account that has an active Copilot seat. Stored encrypted and WRITE-ONLY — it is ' +
        'never shown again, not even partially, so re-registering replaces it rather than editing it.',
      required: true,
      default: '',
      validators: [requiredTrimmed, fineGrainedGitHubPatValidator],
      type: 'github-copilot-pat',
      placeholder: 'github_pat_…',
      secret: true,
      copilotModes: ['PAT'],
    },
    {
      key: 'appId',
      label: 'GitHub App ID',
      hint: 'Required. The numeric App ID of the GitHub App installed for Copilot requests.',
      required: true,
      default: '',
      validators: [requiredTrimmed],
      type: 'text',
      placeholder: '123456',
      copilotModes: ['GITHUB_APP'],
    },
    {
      key: 'installationId',
      label: 'Installation ID',
      hint: "Required. The App's installation on the organization. Must be installed on ALL repositories.",
      required: true,
      default: '',
      validators: [requiredTrimmed],
      type: 'text',
      placeholder: '78901234',
      copilotModes: ['GITHUB_APP'],
    },
    {
      key: 'privateKeyPem',
      label: 'App private key (PEM)',
      hint: "Required. The App's private key. Encrypted at rest and WRITE-ONLY — never shown again after saving.",
      required: true,
      default: '',
      validators: [requiredTrimmed],
      type: 'sa-key-json',
      placeholder: '-----BEGIN RSA PRIVATE KEY-----\n…\n-----END RSA PRIVATE KEY-----',
      secret: true,
      copilotModes: ['GITHUB_APP'],
    },
    {
      key: 'anchorRepositoryId',
      label: 'Anchor repository ID',
      hint:
        'Required. GitHub requires an installation token to name at least one repository, even ' +
        'though Phoenix never reads it — Copilot access is all the token is used for.',
      required: true,
      default: '',
      validators: [requiredTrimmed],
      type: 'text',
      placeholder: '987654321',
      copilotModes: ['GITHUB_APP'],
    },
    {
      key: 'copilotModels',
      label: 'Available models',
      hint:
        "Discovered from GitHub with this registration's own credential, not chosen here. A Copilot " +
        'Free seat sees exactly one model, auto, which GitHub resolves per call; a paid seat sees ' +
        'whatever that plan exposes.',
      required: false,
      default: '',
      validators: [],
      type: 'github-copilot-models',
    },
  ],
};

/**
 * The Vertex field set for one auth mode: every Vertex field whose `modes` includes `authMode` (or
 * that has no `modes`, i.e. belongs to both). This is the mode-aware view the card renders, validates
 * and serialises against — `PROVIDER_FIELDS[VERTEX_VENDOR_ID]` is the union of both modes and must
 * not be consumed directly for Vertex.
 */
export function vertexFieldsForMode(authMode: VertexAuthMode): readonly ByokFieldSpec[] {
  return PROVIDER_FIELDS[VERTEX_VENDOR_ID].filter(
    (spec) => !spec.modes || spec.modes.includes(authMode),
  );
}

/**
 * The Bedrock field set. Unlike Vertex there is no mode filter — Bedrock has exactly one auth mode —
 * so this is simply the whole descriptor. It exists as a named function so a caller reads the same
 * shape for both descriptor providers and cannot accidentally apply {@link vertexFieldsForMode} to
 * Bedrock (which would silently return an empty list, since no Bedrock field declares `modes`).
 */
export function bedrockFields(): readonly ByokFieldSpec[] {
  return PROVIDER_FIELDS[BEDROCK_VENDOR_ID];
}

/**
 * The GitHub Copilot **registration-form** field set for one auth mode: every Copilot field whose
 * `copilotModes` includes `authMode` (or that has no `copilotModes`, i.e. belongs to both).
 *
 * `PROVIDER_FIELDS[GITHUB_COPILOT_VENDOR_ID]` is the union of both modes PLUS the post-activation
 * `copilotModels` view, and must not be consumed directly — the `copilotModels` row is excluded
 * here because it is not an input: Copilot's models are DISCOVERED by the server after activation
 * (plan D-C5), so rendering it in the form would ask an admin to choose from a list that cannot
 * exist yet.
 *
 * A separate function from {@link vertexFieldsForMode} rather than a shared generic, deliberately:
 * the two vendors' mode vocabularies are disjoint, and a shared one would let
 * `vertexFieldsForMode('PAT')` type-check while silently returning every Vertex field.
 */
export function githubCopilotFieldsForMode(
  authMode: GitHubCopilotFieldAuthMode,
): readonly ByokFieldSpec[] {
  return PROVIDER_FIELDS[GITHUB_COPILOT_VENDOR_ID].filter(
    (spec) =>
      spec.type !== 'github-copilot-models' &&
      (!spec.copilotModes || spec.copilotModes.includes(authMode)),
  );
}

/** The post-activation Copilot model-list field spec — the discovered catalog's label and hint. */
export function githubCopilotModelsField(): ByokFieldSpec | undefined {
  return PROVIDER_FIELDS[GITHUB_COPILOT_VENDOR_ID].find(
    (spec) => spec.type === 'github-copilot-models',
  );
}

/** First human-readable message for a Copilot PAT error, or null when valid. */
export function gitHubCopilotPatErrorText(errors: ValidationErrors | null): string | null {
  if (!errors) return null;
  if (errors['required']) return 'Paste a fine-grained GitHub personal access token.';
  if (errors['classicGitHubPat']) {
    return (
      'That is a classic token (ghp_…). Classic tokens cannot carry the Copilot Requests ' +
      'permission at all — generate a fine-grained token (github_pat_…) instead.'
    );
  }
  if (errors['gitHubPatPrefix']) {
    return 'A fine-grained GitHub token starts with github_pat_.';
  }
  return null;
}

/** Field spec for `key` under `vendor`, or undefined when that vendor does not carry the field. */
export function fieldSpec(vendor: VendorId, key: ByokFieldKey): ByokFieldSpec | undefined {
  return PROVIDER_FIELDS[vendor]?.find((spec) => spec.key === key);
}

/** True when `vendor` carries `key` — i.e. it should be rendered, validated and sent. */
export function vendorHasField(vendor: VendorId, key: ByokFieldKey): boolean {
  return fieldSpec(vendor, key) !== undefined;
}

/** First human-readable message for a deployments-array error, or null when valid. */
export function deploymentsErrorText(errors: ValidationErrors | null): string | null {
  if (!errors) return null;
  if (errors['noDeployments']) {
    return 'Add at least one deployment (deployment name and a model id).';
  }
  if (errors['incompleteDeployment']) {
    return 'Every deployment needs both a deployment name and a model id.';
  }
  if (errors['duplicateDeploymentName']) return 'Deployment names must be unique.';
  if (errors['duplicateLogicalModelId']) return 'Model ids must be unique.';
  return null;
}

/** First human-readable message for a model-bindings-array error, or null when valid. */
export function modelBindingsErrorText(errors: ValidationErrors | null): string | null {
  if (!errors) return null;
  if (errors['noModelBindings']) {
    return 'Add at least one model binding (a publisher model id and a logical model id).';
  }
  if (errors['incompleteModelBinding']) {
    return 'Every binding needs both a publisher model id and a logical model id.';
  }
  if (errors['duplicateProviderModelId']) return 'Publisher model ids must be unique.';
  if (errors['duplicateLogicalModelId']) return 'Logical model ids must be unique.';
  return null;
}
